import type { Component, JSX } from 'solid-js';
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
  untrack,
} from 'solid-js';
import WithLabel from '../WithLabel/WithLabel';

export interface CustomAutoCompleteOption {
  label: string;
  value?: string | number;
}

export interface CustomAutoCompleteProps {
  name: string;
  label: string;
  options: readonly CustomAutoCompleteOption[];
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  // Event callbacks (optional)
  onChange?(value: string): void;
  onSelect?(item: CustomAutoCompleteOption): void;
}

const CustomAutoComplete: Component<CustomAutoCompleteProps> = (
  props
): JSX.Element => {
  let inputRef: HTMLInputElement | undefined;
  const setInputRef = (el: HTMLInputElement | undefined): void => {
    inputRef = el;
  };

  const [isOpen, setIsOpen] = createSignal(false);
  const [innerValue, setInnerValue] = createSignal<string>(
    untrack(() => props.value) ?? untrack(() => props.defaultValue) ?? ''
  );

  // Reactive getters for props
  const getName = (): string =>
    props.name ? props.name : 'custom-autocomplete';
  const getLabel = (): string => props.label;
  const getOptions = (): readonly CustomAutoCompleteOption[] => props.options;
  const getPlaceholder = (): string => props.placeholder ?? '';

  // Sync with value prop (controlled)
  createEffect(() => {
    if (props.value !== undefined) {
      setInnerValue(props.value);
    }
  });

  // Filter options by current input (typeahead)
  const getProcessedString = (input: string): string =>
    input.toLowerCase().split(' ').join('');

  const getFilteredList = (
    list: readonly CustomAutoCompleteOption[],
    inputValue: string
  ): CustomAutoCompleteOption[] => {
    if (!inputValue) {
      return [...list];
    }
    const searchText = getProcessedString(inputValue);
    return list.filter((item) => {
      const source = getProcessedString(item.label);
      return source.indexOf(searchText) !== -1;
    });
  };

  const filteredList = createMemo(() =>
    getFilteredList(getOptions(), innerValue())
  );

  // Event handlers — input
  const handleInputChange: JSX.EventHandlerUnion<
    HTMLInputElement,
    InputEvent
  > = (e) => {
    const target = e.currentTarget as HTMLInputElement;
    setInnerValue(target.value);
    props.onChange?.(target.value);
  };

  const handleFocus: JSX.EventHandlerUnion<
    HTMLInputElement,
    FocusEvent
  > = () => {
    setIsOpen(true);
  };

  const handleBlur = (): void => {
    setIsOpen(false);
  };

  // Event handlers — suggestions
  const selectOption = (item: CustomAutoCompleteOption): void => {
    setInnerValue(item.label);
    props.onSelect?.(item); // Only onSelect on item pick; use onChange only for typing
    if (inputRef) {
      inputRef.blur();
    }
  };

  const handleSuggestionMouseDown = (
    e: MouseEvent,
    item: CustomAutoCompleteOption
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    selectOption(item);
  };

  // Event handlers — toggle button
  const toggleMouseDown: JSX.EventHandlerUnion<
    HTMLButtonElement,
    MouseEvent
  > = () => {
    setIsOpen((prev) => !prev);
  };

  const toggleMouseUp: JSX.EventHandlerUnion<
    HTMLButtonElement,
    MouseEvent
  > = () => {
    if (isOpen()) {
      inputRef?.focus();
    }
  };

  const toggleClick: JSX.EventHandlerUnion<HTMLButtonElement, MouseEvent> = (
    e
  ) => {
    e.preventDefault();
  };

  return (
    <WithLabel name={getName()} label={getLabel()} id={`${getName()}-label`}>
      <div class="input-focus-group">
        <div class="input-button-group">
          <input
            ref={setInputRef}
            type="text"
            id={`${getName()}-input`}
            value={innerValue()}
            onInput={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autocomplete="off"
            placeholder={getPlaceholder()}
            aria-expanded={isOpen()}
            aria-controls={isOpen() ? `${getName()}-suggestions` : undefined}
          />
          <div class="line" />
          <Show when={isOpen()}>
            <div
              id={`${getName()}-suggestions`}
              class="auto-complete"
              data-cy="suggestions"
              role="listbox"
            >
              <ul role="list">
                <For each={filteredList()}>
                  {(item, index) => (
                    <li
                      role="option"
                      data-suggestion-index={index()}
                      onMouseDown={(e) => {
                        handleSuggestionMouseDown(e, item);
                      }}
                    >
                      <div class="select-menu-item">
                        <div>{item.label}</div>
                      </div>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          </Show>
          <button
            type="button"
            class="button tertiary icon-medium icon-square"
            onMouseDown={toggleMouseDown}
            onMouseUp={toggleMouseUp}
            onClick={toggleClick}
            aria-label="Toggle suggestions"
          >
            <i class="icon-font tc-icon-arrow-drop-down" />
          </button>
        </div>
      </div>
    </WithLabel>
  );
};

export default CustomAutoComplete;
